import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { useMemo, useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import { Col, Row, Form, FormFeedback, Label, Button } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import VehicleIdSelect from "../../../components/Common/VehicleIdSelect"
import {
  useCreateVehicleMovement,
  useEditVehicleMovement,
} from "hooks/useVehicleMovements"
import FormikCreatableField from "components/Common/FormikCreatableField"
import { buildVehicleOptionLabel } from "helpers/stock_helpers"
import CustomerSearch from "pages/QuotationPages/Quotation/Customer/CustomerSearch"
import QuotationCustomerForm from "pages/QuotationPages/Quotation/Customer/QuotationCustomerForm"
import { useCustomerSearchAPI } from "hooks/useCustomer"
import { useShowrooms } from "hooks/showroomHook"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import HandoverFieldsModal from "./HandoverFieldsModal"

// customer_info mirrors the customer snapshot stored alongside the movement
const emptyCustomerInfo = () => ({
  id: "",
  name: "",
  mobile: "",
  email: "",
  cuid: "",
  customer_type: "",
  designation: "",
  company_name: "",
  full_address: "",
  bank_name: "",
  branch_name: "",
})

const buildCustomerInfoFromRaw = customer => {
  if (!customer) {
    return emptyCustomerInfo()
  }
  return {
    ...emptyCustomerInfo(),
    id: customer.id || "",
    name: customer.name || "",
    mobile: customer.mobile || "",
    email: customer.email || "",
    cuid: customer.cuid || "",
    customer_type: customer.customer_type || "",
    designation: customer.designation || "",
    company_name: customer.company_name || "",
    full_address: customer.full_address || "",
    bank_name: customer.bank_name || "",
    branch_name: customer.branch_name || "",
  }
}

// company_info captures the showroom identity for self movements
const emptyCompanyInfo = () => ({
  name: "",
  mobile: "",
  email: "",
  proprietor_name: "",
  address: "",
})

const buildCompanyInfoFromShowroom = showroom => {
  if (!showroom) {
    return emptyCompanyInfo()
  }
  return {
    ...emptyCompanyInfo(),
    name: showroom.name || "",
    mobile: showroom.mobile || "",
    email: showroom.email || "",
    proprietor_name: showroom.proprietor_name || "",
    address: showroom.address || "",
  }
}

// vehicle_info keys mirror the backend onboarding payload (VehicleOnboardingService)
const emptyVehicleInfo = () => ({
  chassis_no: "",
  engine_no: "",
  brand: "",
  model: "",
  model_year: "",
  color: "",
  cc: "",
  fuel_type: "",
  tyre_size: "",
  mileage: "",
})

const buildVehicleInfoFromRaw = raw => {
  if (!raw) {
    return emptyVehicleInfo()
  }
  return {
    ...emptyVehicleInfo(),
    chassis_no: raw.full_chassis || "",
    engine_no: raw.full_engine || "",
    brand: raw.brand?.name || "",
    model: raw.model?.name || "",
    model_year: raw.year?.name || "",
    color: raw.exteriorColor?.name || raw.color || "",
    cc: raw.engineCC?.name || raw.engineCc?.name || "",
    fuel_type: raw.engineType?.name || raw.fuelType?.name || "",
    tyre_size: raw.tire_size || "",
    mileage: raw.mileage ?? "",
  }
}

const VehicleMovementAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  movement,
  optionLoading,
  movementTypesData,
  employeesData,
  locationsData,
  lockedVehicle = null,
  lockedCustomer = null,
}) => {
  const { mutateAsync: createMovement, isPending: createLoading } =
    useCreateVehicleMovement()
  const { mutateAsync: editMovement, isPending: editLoading } =
    useEditVehicleMovement()

  const [mileageSuggestion, setMileageSuggestion] = useState(null)
  const [selectedCustomer, setSelectedCustomer] = useState(
    lockedCustomer ?? movement?.customer ?? null
  )
  const [newCustomerDraft, setNewCustomerDraft] = useState(null)
  const [showCustomerForm, setShowCustomerForm] = useState(false)
  const [handoverEnabled, setHandoverEnabled] = useState(!!movement?.handover)
  const [handoverModal, setHandoverModal] = useState(false)
  const [handoverDraft, setHandoverDraft] = useState(movement?.handover ?? null)
  const [showVehicleDetails, setShowVehicleDetails] = useState(false)
  const [showCustomerDetails, setShowCustomerDetails] = useState(false)
  const [showCompanyDetails, setShowCompanyDetails] = useState(false)

  const { customerSearchAPI, loading: isCustomerSearching } =
    useCustomerSearchAPI({ all: true, limit: 5 })

  // Main showroom drives the company_info autofill for self movements
  const { data: showroomsData } = useShowrooms({ all: true })
  const mainShowroom = useMemo(() => {
    const list = Array.isArray(showroomsData?.data) ? showroomsData.data : []
    return list.find(s => !s.parent_id) || list[0] || null
  }, [showroomsData])

  // Vehicle option lists power the quick-onboard dropdowns (names, not ids)
  const {
    isLoading: vehicleOptionsLoading,
    brands: brandsData,
    models: modelsData,
    engineCCs: engineCcsData,
    raw: vehicleOptionsRaw,
  } = useFilteredVehicleOptions({ isAll: true })
  const yearsData = useMemo(
    () => vehicleOptionsRaw?.years ?? [],
    [vehicleOptionsRaw?.years]
  )
  const extColorsData = useMemo(
    () => vehicleOptionsRaw?.exterior_colors ?? [],
    [vehicleOptionsRaw?.exterior_colors]
  )
  const fuelTypesData = useMemo(
    () => vehicleOptionsRaw?.fuel_types ?? [],
    [vehicleOptionsRaw?.fuel_types]
  )

  // Formik validation.
  // Creatable dropdowns submit either an existing id or a new name string,
  // so they are validated as a required non-empty value (not strictly numeric).
  const validationSchema = Yup.object({
    vehicle_id: Yup.mixed().required("Please select vehicle"),
    movement_type_id: Yup.string().required("Please select movement type"),
    movement_by_id: Yup.string().required("Please select movement by"),
    movement_for: Yup.string()
      .oneOf(["self", "customer"])
      .required("Please choose Self or Customer"),
    customer_id: Yup.mixed().nullable(),
    location_from_id: Yup.string().required("Please select location from"),
    location_to_id: Yup.string().required("Please select location to"),
    movement_date: Yup.date().required("Please select movement date"),
    mileage: Yup.number()
      .required("Please enter mileage")
      .min(0, "Mileage must be >= 0")
      .test("not-below-last", function (value) {
        // Mileage may not drop below the vehicle's last known reading,
        // unless this entry is flagged as an odometer correction.
        const baseline = mileageSuggestion?.value
        if (this.parent.mileage_corrected || baseline == null || value == null) {
          return true
        }
        if (value >= Number(baseline)) {
          return true
        }
        return this.createError({
          message: `Mileage cannot be less than the last record (${Number(
            baseline
          ).toLocaleString()} km). Tick 'Odometer corrected' to allow a lower value.`,
        })
      }),
    remarks: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm, setFieldError }) => {
      const payload = {
        ...values,
        vehicle_id: values.vehicle_id?.value || values.vehicle_id,
        customer_id:
          values.movement_for === "customer" ? values.customer_id : null,
        new_customer:
          values.movement_for === "customer" && !values.customer_id
            ? newCustomerDraft
            : null,
      }

      const action = isEdit
        ? editMovement({ id: movement.id, ...payload })
        : createMovement(payload)

      try {
        const res = await action
        toast.success(
          res?.message ??
            `Vehicle movement ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        setNewCustomerDraft(null)
        setSelectedCustomer(null)
        setHandoverEnabled(false)
        setHandoverDraft(null)
        setShowVehicleDetails(false)
        toggleModal()
      } catch (error) {
        console.error(error)
        // Surface a backend mileage rejection inline (edit mode relies on this)
        const mileageError = error?.response?.data?.errors?.mileage?.[0]
        if (mileageError) {
          setFieldError("mileage", mileageError)
        } else {
          toast.error("Failed to save vehicle movement.")
        }
      }
    },
    [isEdit, movement, editMovement, createMovement, toggleModal, newCustomerDraft]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vehicle_id: (movement?.vehicle || lockedVehicle)?.id
        ? {
            label: buildVehicleOptionLabel(movement?.vehicle || lockedVehicle),
            value: (movement?.vehicle || lockedVehicle).id,
          }
        : null,
      movement_type_id: movement?.movement_type_id || "",
      movement_by_id: movement?.movement_by_id || "",
      movement_for: lockedCustomer ? "customer" : movement?.movement_for || "self",
      customer_id: lockedCustomer
        ? lockedCustomer.id
        : movement?.customer_id || null,
      location_from_id: movement?.location_from_id || "",
      location_to_id: movement?.location_to_id || "",
      movement_date: movement?.movement_date || "",
      mileage: movement?.mileage || "",
      mileage_corrected: movement?.mileage_corrected ?? false,
      remarks: movement?.remarks || "",
      handover: movement?.handover ?? null,
      brta_inspection: movement?.brta_inspection ?? false,
      vehicle_info: buildVehicleInfoFromRaw(movement?.vehicle || lockedVehicle),
      customer_info: movement?.customer_info
        ? { ...emptyCustomerInfo(), ...movement.customer_info }
        : buildCustomerInfoFromRaw(lockedCustomer ?? movement?.customer),
      company_info: movement?.company_info
        ? { ...emptyCompanyInfo(), ...movement.company_info }
        : emptyCompanyInfo(),
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  // Reset the mileage hint whenever a different movement is loaded.
  // vehicle_id itself is handled by initialValues + enableReinitialize, so it
  // keeps its { label, value } object — needed for VehicleIdSelect to show the
  // proper label instead of the raw id.
  useEffect(() => {
    setMileageSuggestion(null)
    setSelectedCustomer(lockedCustomer ?? movement?.customer ?? null)
    setNewCustomerDraft(null)
    setHandoverEnabled(!!movement?.handover)
    setHandoverDraft(movement?.handover ?? null)
    setShowVehicleDetails(false)
    setShowCustomerDetails(false)
    setShowCompanyDetails(false)
  }, [movement, lockedCustomer])

  // Autofill company_info from the main showroom for self movements when the
  // snapshot is still empty (skips edits that already carry a saved snapshot)
  useEffect(() => {
    if (
      formik.values.movement_for !== "self" ||
      !mainShowroom ||
      formik.values.company_info?.name
    ) {
      return
    }
    formik.setFieldValue("company_info", buildCompanyInfoFromShowroom(mainShowroom))
  }, [mainShowroom, formik.values.movement_for])

  // Suggest mileage from the vehicle's last movement, or fall back to the
  // vehicle's own mileage when it has no movement history (add mode only)
  const handleVehicleSelect = useCallback(
    vehicle => {
      formik.setFieldValue("vehicle_info", buildVehicleInfoFromRaw(vehicle))
      if (vehicle) {
        setShowVehicleDetails(true)
      }

      if (isEdit) {
        return
      }

      if (!vehicle) {
        setMileageSuggestion(null)
        return
      }

      const hasValue = value =>
        value !== null && value !== undefined && value !== ""

      const fromMovement = hasValue(vehicle?.last_movement_mileage)
      const suggested = fromMovement
        ? vehicle.last_movement_mileage
        : vehicle?.mileage

      setMileageSuggestion(
        hasValue(suggested)
          ? {
              value: suggested,
              source: fromMovement ? "last movement" : "vehicle record",
            }
          : null
      )

      if (hasValue(suggested)) {
        formik.setFieldValue("mileage", suggested)
      }
    },
    [isEdit, formik]
  )

  // Memoize button label and state
  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Vehicle Movement"}${
      loading ? "..." : ""
    }`
  }, [isEdit, editLoading, createLoading])

  // Submit reflects only the save request — not the shared options query,
  // otherwise a slow `/options-with-counts` keeps the button disabled.
  const isSubmitDisabled = useMemo(
    () => editLoading || createLoading,
    [editLoading, createLoading]
  )

  const handleNewCustomerSubmit = useCallback(
    values => {
      setNewCustomerDraft(values)
      setSelectedCustomer({
        name: values?.name,
        mobile: values?.mobile,
        customer_type: values?.customer_type,
        pending: true,
      })
      formik.setFieldValue("customer_id", null)
      formik.setFieldValue("customer_info", buildCustomerInfoFromRaw(values))
      setShowCustomerDetails(true)
      toast.info("Customer details captured. Will onboard on save.")
    },
    [formik]
  )

  return (
    <>
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Vehicle Movement" : "Add Vehicle Movement"}
      size="lg"
    >
      <Form
        onSubmit={async e => {
          e.preventDefault()
          if (
            formik.values.movement_for === "customer" &&
            !formik.values.customer_id &&
            !newCustomerDraft
          ) {
            formik.setFieldError(
              "customer_id",
              "Please select or add a customer"
            )
            toast.error("Please select or add a customer")
            return
          }
          const errors = await formik.validateForm()
          const errorKeys = Object.keys(errors)
          if (errorKeys.length > 0) {
            formik.setTouched(
              errorKeys.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
              false
            )
            const firstError = errors[errorKeys[0]]
            toast.error(
              typeof firstError === "string"
                ? firstError
                : "Please fix the highlighted fields and try again."
            )
            return
          }
          formik.handleSubmit()
        }}
      >
        <Row className="mb-3">
          <Col sm={12} className="mb-3">
            {lockedVehicle ? (
              <>
                <Label>Vehicle ID</Label>
                <InputField
                  type="text"
                  disabled
                  readOnly
                  value={buildVehicleOptionLabel(lockedVehicle)}
                />
              </>
            ) : (
              <VehicleIdSelect
                formik={formik}
                onVehicleSelect={handleVehicleSelect}
              />
            )}
          </Col>

          {!lockedVehicle && (
            <Col sm={12} className="mb-3">
              <Button
                color="link"
                size="sm"
                type="button"
                className="p-0 mb-2"
                onClick={() => setShowVehicleDetails(v => !v)}
              >
                {showVehicleDetails ? "Hide" : "Edit"} vehicle details
              </Button>

              {showVehicleDetails && (
                <div className="card mb-0">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">Vehicle Details</h6>
                  </div>
                  <div className="card-body">
                    <Row>
                      <Col sm={12} md={4} className="mb-3">
                        <FormikCreatableField
                          name="vehicle_info.brand"
                          label="Brand"
                          formik={formik}
                          data={brandsData}
                          valueKey="name"
                          labelKey="name"
                          isLoading={vehicleOptionsLoading}
                          placeholder="Select Brand"
                        />
                      </Col>
                      <Col sm={12} md={4} className="mb-3">
                        <FormikCreatableField
                          name="vehicle_info.model"
                          label="Model"
                          formik={formik}
                          data={modelsData}
                          valueKey="name"
                          labelKey="name"
                          isLoading={vehicleOptionsLoading}
                          placeholder="Select Model"
                        />
                      </Col>
                      <Col sm={12} md={4} className="mb-3">
                        <FormikCreatableField
                          name="vehicle_info.model_year"
                          label="Year / Model"
                          formik={formik}
                          data={yearsData}
                          valueKey="name"
                          labelKey="name"
                          isLoading={vehicleOptionsLoading}
                          placeholder="Select Year"
                        />
                      </Col>
                      <Col sm={12} md={4} className="mb-3">
                        <FormikCreatableField
                          name="vehicle_info.cc"
                          label="Engine CC"
                          formik={formik}
                          data={engineCcsData}
                          valueKey="name"
                          labelKey="name"
                          isLoading={vehicleOptionsLoading}
                          placeholder="Select Engine CC"
                        />
                      </Col>
                      <Col sm={12} md={4} className="mb-3">
                        <FormikCreatableField
                          name="vehicle_info.fuel_type"
                          label="Fuel Type"
                          formik={formik}
                          data={fuelTypesData}
                          valueKey="name"
                          labelKey="name"
                          isLoading={vehicleOptionsLoading}
                          placeholder="Select Fuel Type"
                        />
                      </Col>
                      <Col sm={12} md={4} className="mb-3">
                        <FormikCreatableField
                          name="vehicle_info.color"
                          label="Color"
                          formik={formik}
                          data={extColorsData}
                          valueKey="name"
                          labelKey="name"
                          isLoading={vehicleOptionsLoading}
                          placeholder="Select Color"
                        />
                      </Col>
                      <Col sm={12} md={4} className="mb-3">
                        <Label htmlFor="vehicle_info.chassis_no">
                          Chassis No.
                        </Label>
                        <InputField
                          name="vehicle_info.chassis_no"
                          value={formik.values.vehicle_info?.chassis_no || ""}
                          onChange={formik.handleChange}
                        />
                      </Col>
                      <Col sm={12} md={4} className="mb-3">
                        <Label htmlFor="vehicle_info.engine_no">
                          Engine No.
                        </Label>
                        <InputField
                          name="vehicle_info.engine_no"
                          value={formik.values.vehicle_info?.engine_no || ""}
                          onChange={formik.handleChange}
                        />
                      </Col>
                      <Col sm={12} md={4} className="mb-3">
                        <Label htmlFor="vehicle_info.tyre_size">
                          Tyre Size
                        </Label>
                        <InputField
                          name="vehicle_info.tyre_size"
                          value={formik.values.vehicle_info?.tyre_size || ""}
                          onChange={formik.handleChange}
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
              )}
            </Col>
          )}

          {lockedCustomer ? (
            <Col sm={12} className="mb-3">
              <Label>Customer</Label>
              <div className="border rounded p-2 bg-light">
                <div className="fw-semibold">{lockedCustomer.name}</div>
                <small className="text-muted">
                  {lockedCustomer.mobile || "-"}
                </small>
              </div>
            </Col>
          ) : (
            <Col sm={12} className="mb-3">
              <Label className="d-block">Movement For</Label>
              <div className="btn-group" role="group">
                <Button
                  type="button"
                  color={
                    formik.values.movement_for === "self"
                      ? "primary"
                      : "outline-primary"
                  }
                  onClick={() => {
                    formik.setFieldValue("movement_for", "self")
                    formik.setFieldValue("customer_id", null)
                    formik.setFieldValue("customer_info", emptyCustomerInfo())
                    setSelectedCustomer(null)
                    setNewCustomerDraft(null)
                  }}
                >
                  <i className="bx bx-buildings me-1" /> Self
                </Button>
                <Button
                  type="button"
                  color={
                    formik.values.movement_for === "customer"
                      ? "primary"
                      : "outline-primary"
                  }
                  onClick={() =>
                    formik.setFieldValue("movement_for", "customer")
                  }
                >
                  <i className="bx bx-user me-1" /> Customer
                </Button>
              </div>
            </Col>
          )}

          {!lockedCustomer && formik.values.movement_for === "customer" && (
            <Col sm={12} className="mb-3">
              <Label>
                Customer <span className="text-danger">*</span>
              </Label>
              {selectedCustomer ? (
                <div className="d-flex align-items-center justify-content-between border rounded p-2">
                  <div>
                    <div className="fw-semibold">{selectedCustomer.name}</div>
                    <small className="text-muted">
                      {selectedCustomer.mobile || "-"}
                    </small>
                  </div>
                  <Button
                    size="sm"
                    color="outline-secondary"
                    type="button"
                    onClick={() => {
                      setSelectedCustomer(null)
                      setNewCustomerDraft(null)
                      formik.setFieldValue("customer_id", null)
                      formik.setFieldValue("customer_info", emptyCustomerInfo())
                    }}
                  >
                    <i className="bx bx-x me-1" /> Change
                  </Button>
                </div>
              ) : (
                <Row className="g-2">
                  <Col xs={8}>
                    <CustomerSearch
                      onCustomerSelect={c => {
                        setSelectedCustomer(c)
                        setNewCustomerDraft(null)
                        formik.setFieldValue("customer_id", c.id, true)
                        formik.setFieldValue(
                          "customer_info",
                          buildCustomerInfoFromRaw(c)
                        )
                        setShowCustomerDetails(true)
                      }}
                      apiSearchFunction={customerSearchAPI}
                      isSearching={isCustomerSearching}
                      placeholder="Search existing customers..."
                    />
                  </Col>
                  <Col xs={4}>
                    <Button
                      color="primary"
                      className="w-100"
                      type="button"
                      onClick={() => setShowCustomerForm(true)}
                    >
                      <i className="bx bx-user-plus me-1" /> New Customer
                    </Button>
                  </Col>
                </Row>
              )}
              {formik.errors.customer_id && (
                <FormFeedback type="invalid" className="d-block">
                  {formik.errors.customer_id}
                </FormFeedback>
              )}
            </Col>
          )}

          {(lockedCustomer || formik.values.movement_for === "customer") && (
            <Col sm={12} className="mb-3">
              <Button
                color="link"
                size="sm"
                type="button"
                className="p-0 mb-2"
                onClick={() => setShowCustomerDetails(v => !v)}
              >
                {showCustomerDetails ? "Hide" : "Edit"} customer details
              </Button>

              {showCustomerDetails && (
                <div className="border rounded p-3 bg-light">
                  <Row className="g-2">
                    <Col md={6}>
                      <Label>Name</Label>
                      <InputField
                        name="customer_info.name"
                        value={formik.values.customer_info?.name || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Mobile</Label>
                      <InputField
                        name="customer_info.mobile"
                        value={formik.values.customer_info?.mobile || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Email</Label>
                      <InputField
                        type="email"
                        name="customer_info.email"
                        value={formik.values.customer_info?.email || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>CUID</Label>
                      <InputField
                        name="customer_info.cuid"
                        value={formik.values.customer_info?.cuid || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Customer Type</Label>
                      <InputField
                        name="customer_info.customer_type"
                        value={formik.values.customer_info?.customer_type || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Designation</Label>
                      <InputField
                        name="customer_info.designation"
                        value={formik.values.customer_info?.designation || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Company Name</Label>
                      <InputField
                        name="customer_info.company_name"
                        value={formik.values.customer_info?.company_name || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Bank Name</Label>
                      <InputField
                        name="customer_info.bank_name"
                        value={formik.values.customer_info?.bank_name || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Branch Name</Label>
                      <InputField
                        name="customer_info.branch_name"
                        value={formik.values.customer_info?.branch_name || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={12}>
                      <Label>Address</Label>
                      <InputField
                        type="textarea"
                        name="customer_info.full_address"
                        value={formik.values.customer_info?.full_address || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>
                </div>
              )}
            </Col>
          )}

          {!lockedCustomer && formik.values.movement_for === "self" && (
            <Col sm={12} className="mb-3">
              <Button
                color="link"
                size="sm"
                type="button"
                className="p-0 mb-2"
                onClick={() => setShowCompanyDetails(v => !v)}
              >
                {showCompanyDetails ? "Hide" : "Edit"} company details
              </Button>

              {showCompanyDetails && (
                <div className="border rounded p-3 bg-light">
                  <Row className="g-2">
                    <Col md={6}>
                      <Label>Company Name</Label>
                      <InputField
                        name="company_info.name"
                        value={formik.values.company_info?.name || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Proprietor</Label>
                      <InputField
                        name="company_info.proprietor_name"
                        value={formik.values.company_info?.proprietor_name || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Mobile</Label>
                      <InputField
                        name="company_info.mobile"
                        value={formik.values.company_info?.mobile || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Email</Label>
                      <InputField
                        type="email"
                        name="company_info.email"
                        value={formik.values.company_info?.email || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={12}>
                      <Label>Address</Label>
                      <InputField
                        type="textarea"
                        name="company_info.address"
                        value={formik.values.company_info?.address || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>
                </div>
              )}
            </Col>
          )}

          <Col sm={12}>
            <FormikCreatableField
              name="movement_type_id"
              label="Movement Type"
              formik={formik}
              data={movementTypesData}
              isLoading={optionLoading}
              placeholder="Select Type"
            />
            {formik.touched.movement_type_id &&
              formik.errors.movement_type_id && (
                <FormFeedback type="invalid">
                  {formik.errors.movement_type_id}
                </FormFeedback>
              )}
          </Col>

          <Col sm={12} className="mb-3">
            <FormikCreatableField
              name="movement_by_id"
              label="Movement By"
              formik={formik}
              data={employeesData}
              isLoading={optionLoading}
              placeholder="Select a staff"
            />
            {formik.touched.movement_by_id && formik.errors.movement_by_id && (
              <FormFeedback type="invalid">
                {formik.errors.movement_by_id}
              </FormFeedback>
            )}
          </Col>

          <Col sm={6} className="mb-3">
            <FormikCreatableField
              name="location_from_id"
              label="Location From"
              formik={formik}
              data={locationsData}
              isLoading={optionLoading}
              placeholder="Select From"
            />
            {formik.touched.location_from_id &&
              formik.errors.location_from_id && (
                <FormFeedback type="invalid">
                  {formik.errors.location_from_id}
                </FormFeedback>
              )}
          </Col>

          <Col sm={6} className="mb-3">
            <FormikCreatableField
              name="location_to_id"
              label="Location To"
              formik={formik}
              data={locationsData}
              isLoading={optionLoading}
              placeholder="Select To"
            />
            {formik.touched.location_to_id && formik.errors.location_to_id && (
              <FormFeedback type="invalid">
                {formik.errors.location_to_id}
              </FormFeedback>
            )}
          </Col>

          <Col sm={6} className="mb-3">
            <Label htmlFor="movement_date">Movement Date</Label>
            <InputField
              type="datetime-local"
              name="movement_date"
              id="movement_date"
              value={formik.values.movement_date}
              onChange={formik.handleChange}
              invalid={
                formik.touched.movement_date && !!formik.errors.movement_date
              }
            />
            {formik.touched.movement_date && formik.errors.movement_date && (
              <FormFeedback type="invalid">
                {formik.errors.movement_date}
              </FormFeedback>
            )}
          </Col>

          <Col sm={6} className="mb-3">
            <Label htmlFor="mileage">Mileage</Label>
            <InputField
              type="number"
              name="mileage"
              id="mileage"
              value={formik.values.mileage}
              onChange={e => {
                // Validate mileage live while typing
                formik.setFieldValue("mileage", e.target.value, true)
                formik.setFieldTouched("mileage", true, false)
              }}
              onBlur={() => formik.setFieldTouched("mileage", true, true)}
              invalid={formik.touched.mileage && !!formik.errors.mileage}
            />
            {formik.touched.mileage && formik.errors.mileage && (
              <FormFeedback type="invalid">
                {formik.errors.mileage}
              </FormFeedback>
            )}
            {mileageSuggestion && (
              <small className="text-muted">
                Suggested from {mileageSuggestion.source}:{" "}
                {Number(mileageSuggestion.value).toLocaleString()} km
              </small>
            )}
          </Col>

          <Col sm={12} className="mb-3">
            <Button
              type="button"
              size="sm"
              color="secondary"
              outline={!formik.values.mileage_corrected}
              onClick={() =>
                formik.setFieldValue(
                  "mileage_corrected",
                  !formik.values.mileage_corrected,
                  true
                )
              }
            >
              <i
                className={`bx ${
                  formik.values.mileage_corrected
                    ? "bx-check-square"
                    : "bx-square"
                } me-1`}
              />
              Odometer corrected (allow mileage lower than last record)
            </Button>
          </Col>

          <Col sm={12} className="mb-3">
            <Label className="d-block">Hand Over</Label>
            <Button
              type="button"
              color={handoverEnabled ? "primary" : "outline-primary"}
              onClick={() => {
                if (handoverEnabled) {
                  setHandoverEnabled(false)
                  setHandoverDraft(null)
                  formik.setFieldValue("handover", null)
                } else {
                  setHandoverModal(true)
                }
              }}
            >
              <i className="bx bx-transfer me-1" />
              {handoverEnabled ? "Hand Over (On)" : "Hand Over (Off)"}
            </Button>
            {handoverEnabled && (
              <Button
                type="button"
                size="sm"
                color="link"
                className="ms-2"
                onClick={() => setHandoverModal(true)}
              >
                <i className="bx bx-edit me-1" /> Edit Details
              </Button>
            )}

            {handoverEnabled && handoverDraft && (
              <div className="border rounded p-3 bg-light mt-2">
                <Row className="g-3">
                  <Col md={6}>
                    <div className="fw-semibold text-muted small mb-1">
                      Sender
                    </div>
                    <div>{handoverDraft.sender?.name || "-"}</div>
                    <small className="text-muted d-block">
                      {handoverDraft.sender?.mobile || "-"}
                      {handoverDraft.sender?.designation
                        ? ` · ${handoverDraft.sender.designation}`
                        : ""}
                    </small>
                  </Col>
                  <Col md={6}>
                    <div className="fw-semibold text-muted small mb-1">
                      Receiver
                    </div>
                    <div>{handoverDraft.receiver?.name || "-"}</div>
                    <small className="text-muted d-block">
                      {handoverDraft.receiver?.mobile || "-"}
                      {handoverDraft.receiver?.designation
                        ? ` · ${handoverDraft.receiver.designation}`
                        : ""}
                    </small>
                    {handoverDraft.receiver?.company && (
                      <small className="text-muted d-block">
                        {handoverDraft.receiver.company}
                        {handoverDraft.receiver?.company_mobile
                          ? ` · ${handoverDraft.receiver.company_mobile}`
                          : ""}
                      </small>
                    )}
                    {(handoverDraft.receiver?.driver_photo ||
                      handoverDraft.receiver?.driving_licence) && (
                      <small className="text-muted d-block">
                        {[
                          handoverDraft.receiver?.driver_photo
                            ? "Driver photo saved"
                            : null,
                          handoverDraft.receiver?.driving_licence
                            ? "Driving licence saved"
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" / ")}
                      </small>
                    )}
                  </Col>
                  {(handoverDraft.issues || []).some(Boolean) && (
                    <Col sm={12}>
                      <div className="fw-semibold text-muted small mb-1">
                        Issues
                      </div>
                      <ul className="mb-0 small ps-3">
                        {handoverDraft.issues
                          .filter(Boolean)
                          .map((issue, idx) => (
                            <li key={idx}>{issue}</li>
                          ))}
                      </ul>
                    </Col>
                  )}
                </Row>
              </div>
            )}
          </Col>

          <Col sm={12} className="mb-3">
            <Label className="d-block">BRTA Inspection</Label>
            <Button
              type="button"
              color={
                formik.values.brta_inspection ? "primary" : "outline-primary"
              }
              onClick={() =>
                formik.setFieldValue(
                  "brta_inspection",
                  !formik.values.brta_inspection,
                  false
                )
              }
            >
              <i className="bx bx-file me-1" />
              {formik.values.brta_inspection
                ? "BRTA Inspection (On)"
                : "BRTA Inspection (Off)"}
            </Button>
          </Col>
        </Row>

        <div className="mb-3">
          <Label htmlFor="remarks">Remarks</Label>
          <InputField
            type="textarea"
            name="remarks"
            id="remarks"
            value={formik.values.remarks}
            onChange={formik.handleChange}
            invalid={formik.touched.remarks && !!formik.errors.remarks}
          />
          {formik.touched.remarks && formik.errors.remarks && (
            <FormFeedback type="invalid">{formik.errors.remarks}</FormFeedback>
          )}
        </div>

        <div className="text-end">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>

    <QuotationCustomerForm
      isOpen={showCustomerForm}
      toggle={() => setShowCustomerForm(prev => !prev)}
      onCustomer={handleNewCustomerSubmit}
    />

    <HandoverFieldsModal
      isOpen={handoverModal}
      toggle={() => setHandoverModal(false)}
      initialData={handoverDraft}
      employeesData={employeesData}
      onConfirm={data => {
        setHandoverDraft(data)
        setHandoverEnabled(true)
        formik.setFieldValue("handover", data)
      }}
    />
    </>
  )
}

export default VehicleMovementAddEdit
